// The geometry of a map's drawn surfaces, without Three.js (it runs in the map
// loading worker): surfaces batched per (shader, lightmap page, fog), patches
// tessellated, triangles oriented to their vertex normals, vertex colours
// overbright-shifted. World batches keep each surface's index range and
// bounds so the PVS can rebuild their index buffers.

import { SurfaceType, type Bsp, type BspSurface } from '../shared/bsp';
import type { Entity } from '../shared/entities';
import { entityVec, inlineModel } from '../shared/entities';
import { SURF_NODRAW, SURF_SKY } from '../shared/surfaceflags';
import { colorShift } from './colorShift';
import type { LightmapLayout } from './lightmapAtlas';
import type { ShaderDef } from './shaderScripts';

/** Brush entities that are never drawn. */
export const INVISIBLE_CLASSES = /^(trigger_|func_group$|func_invisible_user$|func_constructible$|func_fakebrush$|info_|target_)/;

const PATCH_LEVEL = 8;
/** Overbright shift applied to lightmaps and vertex colors (r_mapOverBrightBits 2, no hw gamma). */
export const LIGHT_SHIFT = 2;

export interface GeometryBatch {
  /** Sky surfaces: drawn with the sky shader (farbox, clouds) per pixel. */
  sky: boolean;
  shaderNum: number;
  /** The BSP lightmap of the surfaces (-1 none, -3 vertex lit); with an atlas, its first lightmap. */
  lightmapNum: number;
  /** Lightmap atlas page (-1 without an atlas). */
  page: number;
  /** BSP fog volume (-1 none, -2 the global fog). */
  fogNum: number;
  pos: Float32Array;
  st: Float32Array;
  lm: Float32Array;
  normal: Float32Array;
  color: Float32Array;
  index: Uint32Array;
  /** Per surface: [surface index, first index, index count] (flat triples). */
  ranges: Int32Array;
  /** Per surface: mins xyz, maxs xyz (flat). */
  bounds: Float32Array;
}

export interface ModelGeometry {
  model: number;
  /** -1 for the world. */
  entityNum: number;
  origin: number[] | null;
  batches: GeometryBatch[];
}

export interface MapGeometry {
  models: ModelGeometry[];
  /** Sky surfaces drawn (none: the global sky box is used). */
  skySurfaces: number;
}

/** The typed arrays of a geometry (to transfer it from a worker). */
export function geometryBuffers(g: MapGeometry): ArrayBuffer[] {
  const out: ArrayBuffer[] = [];
  for (const m of g.models) for (const b of m.batches) for (const a of [b.pos, b.st, b.lm, b.normal, b.color, b.index, b.ranges, b.bounds]) out.push(a.buffer as ArrayBuffer);
  return out;
}


interface Building {
  sky: boolean;
  shaderNum: number;
  lightmapNum: number;
  page: number;
  fogNum: number;
  pos: number[];
  st: number[];
  lm: number[];
  normal: number[];
  color: number[];
  index: number[];
  ranges: number[];
  bounds: number[];
}

export interface GeometryOptions {
  bsp: Bsp;
  shaders: Map<string, ShaderDef>;
  entities: Entity[];
  /** A surface's fog (-1 none, -2 global). */
  fogOf: (surf: BspSurface) => number;
  /** Lightmaps packed into atlas pages: lightmap uv are remapped and batches merge across lightmaps. */
  atlas?: LightmapLayout | null;
  /** Brush entities the map script hides at the start (not drawn). */
  hidden?: Set<number>;
}

export class GeometryBuilder {
  skySurfaces = 0;

  constructor(private readonly o: GeometryOptions) {}

  private shaderDef(shaderNum: number): ShaderDef | undefined {
    const name = this.o.bsp.shaders[shaderNum]?.name;
    return name ? this.o.shaders.get(name) : undefined;
  }

  /** Is the surface drawn as sky (its own skyparms, like Quake 3: maps may use several skies)? */
  isSky(surf: BspSurface): boolean {
    const shader = this.o.bsp.shaders[surf.shaderNum];
    if (!shader || (surf.surfaceType !== SurfaceType.Planar && surf.surfaceType !== SurfaceType.TriangleSoup && surf.surfaceType !== SurfaceType.Patch)) return false;
    const def = this.shaderDef(surf.shaderNum);
    return !!def?.sky || (shader.surfaceFlags & SURF_SKY) !== 0 || !!def?.surfaceParms.has('sky');
  }

  isDrawn(surf: BspSurface): boolean {
    if (surf.surfaceType !== SurfaceType.Planar && surf.surfaceType !== SurfaceType.TriangleSoup && surf.surfaceType !== SurfaceType.Patch) return false;
    const shader = this.o.bsp.shaders[surf.shaderNum];
    if (!shader) return false;
    if (shader.surfaceFlags & (SURF_NODRAW | SURF_SKY)) return false;
    const def = this.shaderDef(surf.shaderNum);
    if (def) {
      if (def.sky !== null || def.surfaceParms.has('sky') || def.surfaceParms.has('nodraw') || def.surfaceParms.has('fog')) return false;
      if (def.surfaceParms.has('trigger') || def.surfaceParms.has('hint')) return false;
    }
    const name = shader.name;
    if (name === 'noshader' || name.startsWith('textures/common/')) {
      // common/* tool textures without a renderable stage are editor-only
      if (!def || !def.stages.some((s) => s.map && !s.map.startsWith('$'))) return false;
    }
    return true;
  }

  /** The world and every drawn brush entity. */
  build(): MapGeometry {
    const models: ModelGeometry[] = [];
    const world = this.buildModel(0, null, -1);
    if (world) models.push(world);
    this.o.entities.forEach((ent, entNum) => {
      const m = inlineModel(ent);
      if (m <= 0 || INVISIBLE_CLASSES.test(ent.classname ?? '') || this.o.hidden?.has(entNum)) return;
      const g = this.buildModel(m, ent.origin ? entityVec(ent, 'origin') : null, entNum);
      if (g) models.push(g);
    });
    return { models, skySurfaces: this.skySurfaces };
  }

  buildModel(modelIndex: number, origin: number[] | null, entityNum: number): ModelGeometry | null {
    const { bsp, atlas } = this.o;
    const model = bsp.models[modelIndex];
    if (!model) return null;
    const batches = new Map<string, Building>();
    const batchFor = (surf: BspSurface, sky: boolean): Building => {
      const fogNum = sky ? -1 : this.o.fogOf(surf);
      // with an atlas the lightmaps of one page draw together
      const tile = atlas && surf.lightmapNum >= 0 ? atlas.tiles[surf.lightmapNum] : undefined;
      const page = tile ? tile.page : -1;
      const lmKey = tile ? `p${page}` : String(surf.lightmapNum);
      const key = sky ? `sky:${surf.shaderNum}` : `${surf.shaderNum}:${lmKey}:${fogNum}`;
      let b = batches.get(key);
      if (!b) {
        b = { sky, shaderNum: surf.shaderNum, lightmapNum: surf.lightmapNum, page, fogNum, pos: [], st: [], lm: [], normal: [], color: [], index: [], ranges: [], bounds: [] };
        batches.set(key, b);
      }
      return b;
    };

    for (let s = model.firstSurface; s < model.firstSurface + model.numSurfaces; s++) {
      const surf = bsp.surfaces[s];
      const sky = this.isSky(surf);
      if (!sky && !this.isDrawn(surf)) continue;
      if (sky) this.skySurfaces++;
      const batch = batchFor(surf, sky);
      const firstIndex = batch.index.length;
      const firstVert = batch.pos.length / 3;
      if (surf.surfaceType === SurfaceType.Patch) this.addPatch(batch, surf);
      else this.addTriangles(batch, surf);
      if (batch.index.length === firstIndex) continue;
      batch.ranges.push(s, firstIndex, batch.index.length - firstIndex);
      const mins = [Infinity, Infinity, Infinity];
      const maxs = [-Infinity, -Infinity, -Infinity];
      for (let v = firstVert * 3; v < batch.pos.length; v += 3) {
        for (let k = 0; k < 3; k++) {
          mins[k] = Math.min(mins[k], batch.pos[v + k]);
          maxs[k] = Math.max(maxs[k], batch.pos[v + k]);
        }
      }
      batch.bounds.push(...mins, ...maxs);
    }
    const out: GeometryBatch[] = [];
    for (const b of batches.values()) {
      if (!b.index.length) continue;
      out.push({
        sky: b.sky,
        shaderNum: b.shaderNum,
        lightmapNum: b.lightmapNum,
        page: b.page,
        fogNum: b.fogNum,
        pos: Float32Array.from(b.pos),
        st: Float32Array.from(b.st),
        lm: Float32Array.from(b.lm),
        normal: Float32Array.from(b.normal),
        color: Float32Array.from(b.color),
        index: Uint32Array.from(b.index),
        ranges: Int32Array.from(b.ranges),
        bounds: Float32Array.from(b.bounds),
      });
    }
    return { model: modelIndex, entityNum, origin, batches: out };
  }

  private pushVert(b: Building, lightmapNum: number, p: number[], st: number[], lm: number[], n: number[], c: number[]): number {
    const idx = b.pos.length / 3;
    b.pos.push(p[0], p[1], p[2]);
    b.st.push(st[0], st[1]);
    const tile = this.o.atlas && lightmapNum >= 0 ? this.o.atlas.tiles[lightmapNum] : undefined;
    if (tile) b.lm.push(tile.u0 + lm[0] * tile.su, tile.v0 + lm[1] * tile.sv);
    else b.lm.push(lm[0], lm[1]);
    const len = Math.hypot(n[0], n[1], n[2]) || 1;
    b.normal.push(n[0] / len, n[1] / len, n[2] / len);
    const shifted = [0, 0, 0];
    colorShift(c, 0, shifted, 0, LIGHT_SHIFT);
    b.color.push(shifted[0] / 255, shifted[1] / 255, shifted[2] / 255, c[3] / 255);
    return idx;
  }

  private addTriangles(b: Building, surf: BspSurface): void {
    const { verts, indexes } = this.o.bsp;
    const base = b.pos.length / 3;
    for (let i = 0; i < surf.numVerts; i++) {
      const v = surf.firstVert + i;
      this.pushVert(
        b,
        surf.lightmapNum,
        [verts.xyz[v * 3], verts.xyz[v * 3 + 1], verts.xyz[v * 3 + 2]],
        [verts.st[v * 2], verts.st[v * 2 + 1]],
        [verts.lightmap[v * 2], verts.lightmap[v * 2 + 1]],
        [verts.normal[v * 3], verts.normal[v * 3 + 1], verts.normal[v * 3 + 2]],
        [verts.color[v * 4], verts.color[v * 4 + 1], verts.color[v * 4 + 2], verts.color[v * 4 + 3]],
      );
    }
    for (let i = 0; i + 2 < surf.numIndexes; i += 3) {
      const ia = indexes[surf.firstIndex + i];
      const ib = indexes[surf.firstIndex + i + 1];
      const ic = indexes[surf.firstIndex + i + 2];
      // orient each triangle so its counter-clockwise front matches the vertex normals
      const flip = !this.windingMatchesNormal(surf.firstVert + ia, surf.firstVert + ib, surf.firstVert + ic);
      if (flip) b.index.push(base + ia, base + ic, base + ib);
      else b.index.push(base + ia, base + ib, base + ic);
    }
  }

  private windingMatchesNormal(a: number, b: number, c: number): boolean {
    const { xyz, normal } = this.o.bsp.verts;
    const e1x = xyz[b * 3] - xyz[a * 3];
    const e1y = xyz[b * 3 + 1] - xyz[a * 3 + 1];
    const e1z = xyz[b * 3 + 2] - xyz[a * 3 + 2];
    const e2x = xyz[c * 3] - xyz[a * 3];
    const e2y = xyz[c * 3 + 1] - xyz[a * 3 + 1];
    const e2z = xyz[c * 3 + 2] - xyz[a * 3 + 2];
    const nx = e1y * e2z - e1z * e2y;
    const ny = e1z * e2x - e1x * e2z;
    const nz = e1x * e2y - e1y * e2x;
    const vx = normal[a * 3] + normal[b * 3] + normal[c * 3];
    const vy = normal[a * 3 + 1] + normal[b * 3 + 1] + normal[c * 3 + 1];
    const vz = normal[a * 3 + 2] + normal[b * 3 + 2] + normal[c * 3 + 2];
    const d = nx * vx + ny * vy + nz * vz;
    // Quake 3 BSPs are wound clockwise; keep that assumption when normals are missing.
    return d === 0 ? false : d > 0;
  }

  private addPatch(b: Building, surf: BspSurface): void {
    const { verts } = this.o.bsp;
    const w = surf.patchWidth;
    const h = surf.patchHeight;
    if (w < 3 || h < 3) return;
    const ctrl = (col: number, row: number) => surf.firstVert + row * w + col;
    const L = PATCH_LEVEL;
    const bez = (t: number): [number, number, number] => [(1 - t) * (1 - t), 2 * t * (1 - t), t * t];

    for (let py = 0; py + 2 < h; py += 2) {
      for (let px = 0; px + 2 < w; px += 2) {
        const base = b.pos.length / 3;
        const grid: number[][] = []; // generated vertex normals for winding checks
        for (let j = 0; j <= L; j++) {
          const bv = bez(j / L);
          for (let i = 0; i <= L; i++) {
            const bu = bez(i / L);
            const p = [0, 0, 0];
            const st = [0, 0];
            const lm = [0, 0];
            const c = [0, 0, 0, 0];
            const n = [0, 0, 0];
            for (let y = 0; y < 3; y++) {
              for (let x = 0; x < 3; x++) {
                const wgt = bu[x] * bv[y];
                const v = ctrl(px + x, py + y);
                for (let k = 0; k < 3; k++) {
                  p[k] += verts.xyz[v * 3 + k] * wgt;
                  n[k] += verts.normal[v * 3 + k] * wgt;
                }
                st[0] += verts.st[v * 2] * wgt;
                st[1] += verts.st[v * 2 + 1] * wgt;
                lm[0] += verts.lightmap[v * 2] * wgt;
                lm[1] += verts.lightmap[v * 2 + 1] * wgt;
                for (let k = 0; k < 4; k++) c[k] += verts.color[v * 4 + k] * wgt;
              }
            }
            this.pushVert(b, surf.lightmapNum, p, st, lm, n, c);
            grid.push(n);
          }
        }
        const row = L + 1;
        for (let j = 0; j < L; j++) {
          for (let i = 0; i < L; i++) {
            const a = j * row + i;
            const tris: [number, number, number][] = [
              [a, a + 1, a + row + 1],
              [a, a + row + 1, a + row],
            ];
            for (const [ia, ib, ic] of tris) {
              if (this.patchWindingOk(b, base, ia, ib, ic, grid)) b.index.push(base + ia, base + ib, base + ic);
              else b.index.push(base + ia, base + ic, base + ib);
            }
          }
        }
      }
    }
  }

  private patchWindingOk(b: Building, base: number, ia: number, ib: number, ic: number, normals: number[][]): boolean {
    const P = (i: number) => [b.pos[(base + i) * 3], b.pos[(base + i) * 3 + 1], b.pos[(base + i) * 3 + 2]];
    const A = P(ia);
    const B = P(ib);
    const C = P(ic);
    const e1 = [B[0] - A[0], B[1] - A[1], B[2] - A[2]];
    const e2 = [C[0] - A[0], C[1] - A[1], C[2] - A[2]];
    const n = [e1[1] * e2[2] - e1[2] * e2[1], e1[2] * e2[0] - e1[0] * e2[2], e1[0] * e2[1] - e1[1] * e2[0]];
    const v = [0, 1, 2].map((k) => normals[ia][k] + normals[ib][k] + normals[ic][k]);
    return n[0] * v[0] + n[1] * v[1] + n[2] * v[2] >= 0;
  }
}
